import { Carousel } from "@mantine/carousel";
import { useMediaQuery } from "@mantine/hooks";
import { createStyles, Paper, rem, useMantineTheme } from "@mantine/core";
import * as React from "react";
import { ImageModal } from "../../../components/ImageModal";

const useStyles = createStyles((theme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
  },

  imageContainer: {
    width: "100%",
    overflow: "hidden",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    cursor: "pointer",
    backgroundColor: "#f4f4f4",
    transition: "opacity 0.25s ease",
    "&:hover": {
      opacity: 0.55,
    },
  },

  image: {
    display: "block",
    width: "100%",
    height: "auto",
  },
  
  caption: {
    color: "#000",
    fontFamily: "IBM Plex Mono, monospace",
    fontSize: rem(13),
    textAlign: "center",
    marginTop: rem(10),
    padding: `0 ${rem(8)}`,
  }
}));

interface CardProps {
  image: string;
  caption: string;
  onImageClick: () => void;
}

function Card({ image, caption, onImageClick }: CardProps) {
  const { classes } = useStyles();

  return (
    <Paper className={classes.card}>
      <div className={classes.imageContainer} onClick={onImageClick}>
        <img src={image} alt={caption} className={classes.image} loading="lazy" />
      </div>
    </Paper>
  );
}

interface CardsCarouselProps {
  images: { image: string; caption: string }[];
}

export function CardsCarousel({ images }: CardsCarouselProps) {
  const theme = useMantineTheme();
  const mobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const [modalOpened, setModalOpened] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState<{ image: string; caption: string } | null>(null);
  
  const handleImageClick = (image: string, caption: string) => {
    setSelectedImage({ image, caption });
    setModalOpened(true);
  };

  const handleCloseModal = () => {
    setModalOpened(false);
    setSelectedImage(null);
  };
  
  const slides = images.map((item, index) => (
    <Carousel.Slide key={index}>
      <Card {...item} onImageClick={() => handleImageClick(item.image, item.caption)} />
    </Carousel.Slide>
  ));

  return (
    <>
      <Carousel
        slideSize="33.333%"
        breakpoints={[
          { maxWidth: 'md', slideSize: '50%' },
          { maxWidth: 'sm', slideSize: '80%' },
          { maxWidth: 'xs', slideSize: '100%' },
        ]}
        slideGap={rem(20)}
        align="start"
        slidesToScroll={mobile ? 1 : 2}
        withControls={images.length > 1}
        loop
        styles={{
          control: {
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #000",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#000",
              color: "#fff",
            },
          }
        }}
      >
        {slides}
      </Carousel>
      
      {selectedImage && (
        <ImageModal
          opened={modalOpened}
          onClose={handleCloseModal}
          imageSrc={selectedImage.image}
          caption={selectedImage.caption}
        />
      )}
    </>
  );
} 